import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import Logo from '../components/Logo';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Verify = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const [memberInfo, setMemberInfo] = useState(null);

  useEffect(() => {
    const verifyToken = async () => {
      const token = searchParams.get('token');
      
      if (!token) {
        setStatus('error');
        setMessage('Lien de vérification invalide.');
        return;
      }

      try {
        const response = await axios.get(`${API}/membership/verify?token=${token}`);
        
        if (response.data.success) {
          setStatus('success');
          setMessage(response.data.message);
          setMemberInfo(response.data.member);
          
          // Redirect to home after 5 seconds
          setTimeout(() => {
            navigate('/');
          }, 5000);
        }
      } catch (error) {
        setStatus('error');
        setMessage(
          error.response?.data?.detail || 
          "Une erreur est survenue lors de la vérification. Le lien est peut-être expiré."
        );
      }
    };

    verifyToken();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-amber-50 flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-2xl"
      >
        <div className="text-center mb-8">
          <Logo className="justify-center mb-6" />
        </div>

        <Card className="border-2 border-slate-200 shadow-2xl">
          <CardContent className="p-12">
            {status === 'loading' && (
              <div className="text-center">
                <Loader2 className="w-16 h-16 mx-auto mb-6 text-orange-600 animate-spin" />
                <h2 className="text-3xl font-bold mb-4 text-slate-900">Vérification en cours...</h2>
                <p className="text-slate-600">Veuillez patienter pendant que nous confirmons votre adhésion.</p>
              </div>
            )}

            {status === 'success' && (
              <div className="text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', duration: 0.6 }}
                >
                  <CheckCircle className="w-20 h-20 mx-auto mb-6 text-green-600" />
                </motion.div>
                <h2 className="text-3xl font-bold mb-4 text-slate-900">Adhésion confirmée !</h2>
                <p className="text-lg text-slate-600 mb-6">{message}</p>
                
                {memberInfo && (
                  <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-6 mb-6">
                    <p className="text-slate-700">
                      <span className="font-semibold">Bienvenue, </span>
                      {memberInfo.firstName} {memberInfo.lastName} !
                    </p>
                    <p className="text-sm text-slate-500 mt-2">{memberInfo.email}</p>
                  </div>
                )}

                <p className="text-sm text-slate-500 mb-6">
                  Vous allez être redirigé vers la page d'accueil dans quelques secondes...
                </p>

                <Button
                  onClick={() => navigate('/')}
                  className="bg-gradient-to-r from-orange-600 to-amber-600 text-white hover:shadow-lg"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Retour à l'accueil
                </Button>
              </div>
            )}

            {status === 'error' && (
              <div className="text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', duration: 0.6 }}
                >
                  <XCircle className="w-20 h-20 mx-auto mb-6 text-red-600" />
                </motion.div>
                <h2 className="text-3xl font-bold mb-4 text-slate-900">Vérification échouée</h2>
                <p className="text-lg text-slate-600 mb-8">{message}</p>

                <div className="space-y-4">
                  <Button
                    onClick={() => navigate('/#membership')}
                    className="bg-gradient-to-r from-orange-600 to-amber-600 text-white hover:shadow-lg w-full"
                  >
                    Réessayer l'inscription
                  </Button>
                  <Button
                    onClick={() => navigate('/')}
                    variant="outline"
                    className="border-2 border-orange-600 text-orange-600 hover:bg-orange-50 w-full"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Retour à l'accueil
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default Verify;